import { createStore } from "./createStore";

// Single source of truth for the bite_status domain on the tutor side.
// Unmarked values arrive on the DDB snapshot / bite_metrics (backend-computed,
// mirrored by the Lambda). [UI-only] values are never sent by the server — the
// player derives them client-side and they must NOT be persisted or expected
// back from any API.
export const BITE_STATUS = {
  NOT_STARTED: "not_started", // absence of data for the slide
  CURRENTLY_LEARNING: "currently_learning", // [UI-only] derived at render time from currentSlideIndex; never stored
  INCOMPLETE: "incomplete",
  COMPLETED: "completed",
  SKIPPED_BY_USER: "skipped_by_user",
  SKIPPED_BY_AI: "skipped_by_ai", // currently a dead branch (backend never writes skip_reason="ai")
  PROCESSING: "processing", // [UI-only] optimistic, shown while waiting for change_slide response (~100-300ms)
  ASSESSMENT: "assessment", // [UI-only] set by markAssessmentSlides for exit_ticket/pre_assessment/custom
} as const;

export type BiteStatus = (typeof BITE_STATUS)[keyof typeof BITE_STATUS];

// The backend-computed values the Lambda mirrors into bite_metrics (full domain).
const BITE_STATUS_VALUES = new Set<BiteStatus>([
  BITE_STATUS.INCOMPLETE,
  BITE_STATUS.COMPLETED,
  BITE_STATUS.SKIPPED_BY_USER,
  BITE_STATUS.SKIPPED_BY_AI,
]);

// bite_status is the single completion field (backend-computed, mirrored by the
// Lambda into bite_metrics). It carries the full domain incl. skipped_by_*.
function resolveStatusFromBiteMetrics(val: any): BiteStatus {
  if (!val) return BITE_STATUS.NOT_STARTED;
  // Legacy JSONB rows (pre-refactor) carried skip as a separate skip_reason key
  // with bite_status="incomplete". Honor it so reopened old sessions still
  // render the skip icon instead of collapsing to incomplete.
  if (val.skip_reason === "user") return BITE_STATUS.SKIPPED_BY_USER;
  if (val.skip_reason === "ai") return BITE_STATUS.SKIPPED_BY_AI;
  const status = val.bite_status;
  return BITE_STATUS_VALUES.has(status) ? status : BITE_STATUS.NOT_STARTED;
}

const ASSESSMENT_KEYS_SET = new Set([
  "exit_ticket",
  "pre_assessment",
  "custom",
]);

interface BiteStatusState {
  statuses: Record<number, BiteStatus>;
  isInitialized: boolean;
  setStatus: (slideIndex: number, status: BiteStatus) => void;
  initFromEnsureSession: (biteMetrics: Record<string, any>) => void;
  applyBiteMetrics: (biteMetrics: Record<string, any>) => void;
  markAssessmentSlides: (
    slides: Array<{
      slide_index: number;
      metadata?: { slide_type?: string };
      slide_type?: string;
    }>,
  ) => void;
  clearStatuses: () => void;
}

const base = (set: any, _get: any): BiteStatusState => ({
  statuses: {},
  isInitialized: false,

  setStatus: (slideIndex: number, status: BiteStatus) => {
    set((prev: BiteStatusState) => {
      const existing = prev.statuses[slideIndex];
      if (existing === BITE_STATUS.ASSESSMENT) return prev;
      if (
        status !== BITE_STATUS.CURRENTLY_LEARNING &&
        existing === BITE_STATUS.COMPLETED
      )
        return prev;
      if (
        status === BITE_STATUS.PROCESSING &&
        existing === BITE_STATUS.COMPLETED
      )
        return prev;
      return { statuses: { ...prev.statuses, [slideIndex]: status } };
    });
  },

  // Bulk init from ensure_session response — does not overwrite "assessment".
  // Postgres bite_metrics is the single source of status: the backend now
  // pre-fills it synchronously (change_slide + cleanup), so there is no DDB
  // snapshot fallback anymore.
  initFromEnsureSession: (biteMetrics: Record<string, any>) => {
    const statuses: Record<number, BiteStatus> = {};

    for (const [key, val] of Object.entries(biteMetrics)) {
      statuses[Number(key)] = resolveStatusFromBiteMetrics(val);
    }

    // Don't write currently_learning to store — SlideProgress derives it from currentSlideIndex at render time

    set((prev: BiteStatusState) => {
      const merged = { ...statuses };
      // Preserve any assessment labels already set
      for (const [k, v] of Object.entries(prev.statuses)) {
        if (v === BITE_STATUS.ASSESSMENT)
          merged[Number(k)] = BITE_STATUS.ASSESSMENT;
      }
      return { statuses: merged, isInitialized: true };
    });
  },

  // Apply bite_metrics updates — never downgrades completed/skipped
  applyBiteMetrics: (biteMetrics: Record<string, any>) => {
    set((prev: BiteStatusState) => {
      const updated = { ...prev.statuses };
      for (const [key, val] of Object.entries(biteMetrics)) {
        const idx = Number(key);
        const existing = updated[idx];
        if (
          existing === BITE_STATUS.ASSESSMENT ||
          existing === BITE_STATUS.COMPLETED ||
          existing === BITE_STATUS.SKIPPED_BY_USER ||
          existing === BITE_STATUS.SKIPPED_BY_AI
        )
          continue;
        updated[idx] = resolveStatusFromBiteMetrics(val);
      }
      return { statuses: updated };
    });
  },

  // Mark assessment slides as immutable "assessment" status — call once on slides load
  markAssessmentSlides: (
    slides: Array<{
      slide_index: number;
      metadata?: { slide_type?: string };
      slide_type?: string;
    }>,
  ) => {
    set((prev: BiteStatusState) => {
      const updated = { ...prev.statuses };
      for (const s of slides) {
        const t = s.metadata?.slide_type ?? (s as any).slide_type;
        if (t && ASSESSMENT_KEYS_SET.has(t)) {
          updated[s.slide_index] = BITE_STATUS.ASSESSMENT;
        }
      }
      return { statuses: updated };
    });
  },

  clearStatuses: () => set({ statuses: {}, isInitialized: false }),
});

export const useBiteStatusStore = createStore("BiteStatusStore", base);
